import { cn } from '@/shared/lib';
import { InputErrorMessage } from '@/shared/components';
import { FC, useCallback, useMemo, useState } from 'react';
import { ClientLocationDto, ServiceTypeEnum } from '@/app/store/api/restApi';
import { AddressView, BasketInputs, BasketOptionSwitcher, TakeoutSelector } from '@/features';
import {
  useAppSelector,
  useAppDispatch,
  setTakeoutError,
  changeEatAtPlace,
  openAddressViewModal,
  changeAnotherEatAtThePlace,
  selectIsMinimalistic,
} from '@/app/store';

interface BasketTakeoutPersonalInfoProps {
  takeoutAddress?: string;
  locations: ClientLocationDto[];
  isOrderAnotherPerson?: boolean;
  currentLocation?: ClientLocationDto;
}

export const BasketTakeoutPersonalInfo: FC<BasketTakeoutPersonalInfoProps> = ({
  locations,
  takeoutAddress,
  currentLocation,
  isOrderAnotherPerson = false,
}) => {
  const dispatch = useAppDispatch();
  const [showSelector, setShowSelector] = useState(false);

  const { takeoutError } = useAppSelector((state) => state.errors);
  const eatAtThePlace = useAppSelector((state) => state.order.options.eatAtThePlace);
  const anotherEatAtThePlace = useAppSelector((state) => state.orderAnotherPerson.eatAtThePlace);
  const { eatAtPlaceOptionEnabled, commentsEnabled } = useAppSelector((state) => state.initial.settings);
  const isMinimalistic = useAppSelector(selectIsMinimalistic);

  const eatAtThePlaceOption = useMemo(
    () => (isOrderAnotherPerson ? anotherEatAtThePlace : eatAtThePlace),
    [anotherEatAtThePlace, eatAtThePlace, isOrderAnotherPerson],
  );

  const handleNavigate = useCallback(() => {
    if (currentLocation) {
      dispatch(
        openAddressViewModal({
          address: currentLocation.address,
          lat: currentLocation.coordinates.lat,
          lng: currentLocation.coordinates.lng,
          areManualCoordinates: currentLocation.areManualCoordinates,
        }),
      );
    }
  }, [currentLocation, dispatch]);

  return (
    <div className={cn('flex flex-col', isMinimalistic ? 'gap-2' : 'gap-3')}>
      <div>
        {showSelector ? (
          <TakeoutSelector
            value={takeoutAddress}
            onChange={() => {
              setShowSelector(false);
              dispatch(setTakeoutError(undefined));
            }}
            onOpenChange={() => setShowSelector(false)}
            variants={locations.map((l) => ({ value: l.id, title: l.name }))}
            className='w-full max-w-[calc(100vw-32px)]'
            selectorClassName={cn('h-10', takeoutError && 'border-destructive')}
            isOrderAnotherPerson={isOrderAnotherPerson}
          />
        ) : (
          <AddressView
            error={!!takeoutError}
            onClickMap={handleNavigate}
            value={currentLocation?.name}
            onClick={() => setShowSelector(true)}
            deliveryType={ServiceTypeEnum.Takeout}
          />
        )}

        {takeoutError && <InputErrorMessage text={takeoutError} />}
      </div>

      <BasketInputs isTakeout commentsEnabled={commentsEnabled} isOrderAnotherPerson={isOrderAnotherPerson} />

      {eatAtPlaceOptionEnabled && (
        <BasketOptionSwitcher
          isTakeout
          checked={eatAtThePlaceOption}
          onCheckedChange={(bool) => {
            if (isOrderAnotherPerson) dispatch(changeAnotherEatAtThePlace(bool));
            else dispatch(changeEatAtPlace(bool));
          }}
        />
      )}
    </div>
  );
};
